import { useState } from "react";
import { X } from "lucide-react";
import { isNative } from "@cntrl/native";
import { Button, Card, CardEmpty, Icon, Modal, Spinner, DialogCancel } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import {
  useOutstanding,
  useOutstandingHistory,
  useSettleBox,
  useMarkCollect,
  useMarkExpense,
  useMarkMemberPaid,
  useUnmarkMemberPaid,
  useCloseOut,
  useReopenScope,
  useVenmoQr,
  type OutstandingCollectItem,
  type OutstandingExpenseItem,
  type OutstandingMemberLine,
} from "../../app/hooks/useMoney.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { ReopenModal } from "./ReopenShowModal.js";
import { VenmoWordmark } from "./VenmoWordmark.js";
import { usdCents } from "./lib/bookingMoney.js";
import { dowOf, dayOf } from "./lib/bookingRows.js";
import { venmoDeepLink, venmoWebLink, venmoNote } from "./lib/venmo.js";
import styles from "./OutstandingPaymentsCard.module.scss";

/**
 * ART-241 — the Booking "Outstanding Payments" card + its settle box. One row per Show / Run / Tour with
 * anything left to settle; clicking a row opens the settle box to COLLECT venue deposits/balances and PAY
 * members their per-diem + day pay.
 *
 * MONEY GROUP ONLY and self-gating (the `BookingKpiStrip` pattern): a seat without `money:full | money:per_show`
 * never mounts the component, so the query never fires — money is a separate, capability-gated surface, never a
 * conditional field. It ships FULL-WIDTH in the shows column for now (R1); the right-column pairing with the
 * Quote Builder is tracked separately.
 */
export function OutstandingPaymentsCard() {
  // Both hooks run unconditionally (no `||` short-circuit — rules-of-hooks).
  const canFull = useCapability("money:full");
  const canPerShow = useCapability("money:per_show");
  const canSee = canFull || canPerShow;
  const [open, setOpen] = useState<{ kind: string; id: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const q = useOutstanding(canSee);

  if (!canSee) return null;

  const list = q.data;
  const rows = list?.rows ?? [];

  return (
    <Card
      id="outstanding"
      title="Outstanding Payments"
      actions={
        <span className={styles.cardActions}>
          {rows.length > 0 && <span className={styles.grand}>{usdCents(list!.grandLeftCents)}</span>}
          <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>History</Button>
        </span>
      }
    >
      {q.isLoading ? (
        <div className={styles.loading}><Spinner size={20} /></div>
      ) : rows.length === 0 ? (
        <CardEmpty>Everything&rsquo;s settled.</CardEmpty>
      ) : (
        <ul className={styles.rows}>
          {rows.map((r) => {
            const total = r.itemsTotal ?? 0;
            const settled = r.itemsSettled ?? 0;
            const pct = total > 0 ? Math.round((settled / total) * 100) : 0;
            const kind = r.scopeKind ?? "";
            const id = r.scopeId ?? "";
            return (
              <li key={`${kind}:${id}`}>
                <button type="button" className={styles.row} onClick={() => setOpen({ kind, id })}>
                  <span className={styles.rowMain}>
                    <span className={styles.rowTitle}>{r.title}</span>
                    {r.sub && <span className={styles.rowSub}>{r.sub}</span>}
                    <span className={styles.bar} aria-hidden="true">
                      <span className={styles.barFill} style={{ inlineSize: `${pct}%` }} />
                    </span>
                  </span>
                  <span className={styles.rowRight}>
                    <span className={styles.rowLeft}>{usdCents(r.leftCents)}</span>
                    <span className={styles.rowCount}>{settled}/{total} settled</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {open && <SettleBox scopeKind={open.kind} scopeId={open.id} onClose={() => setOpen(null)} />}
      {historyOpen && <HistoryModal onClose={() => setHistoryOpen(false)} />}
    </Card>
  );
}

/** The settled/closed-out HISTORY — a full list of finished scopes; clicking one reopens its settle box (where a
 *  payout can be undone or the scope reopened). The active card shows only what still owes money. */
function HistoryModal({ onClose }: { onClose: () => void }) {
  const q = useOutstandingHistory(true);
  const [open, setOpen] = useState<{ kind: string; id: string } | null>(null);
  const rows = q.data?.rows ?? [];

  return (
    <Modal title="Settled history" hideHeader size="lg" bodyLayout="flush" onClose={onClose}>
      <div className={styles.quietHead}>
        <span className={styles.quietTitle}>Settled history</span>
        <button type="button" className={styles.quietClose} aria-label="Close" onClick={onClose}>
          <Icon icon={X} size={20} />
        </button>
      </div>
      {q.isLoading ? (
        <div className={styles.loading}><Spinner size={20} /></div>
      ) : rows.length === 0 ? (
        <div className={styles.historyBody}><CardEmpty>Nothing settled yet.</CardEmpty></div>
      ) : (
        <ul className={styles.historyRows}>
          {rows.map((r) => {
            const kind = r.scopeKind ?? "";
            const id = r.scopeId ?? "";
            return (
              <li key={`${kind}:${id}`}>
                <button type="button" className={styles.row} onClick={() => setOpen({ kind, id })}>
                  <span className={styles.rowMain}>
                    <span className={styles.rowTitle}>{r.title}</span>
                    {r.sub && <span className={styles.rowSub}>{r.sub}</span>}
                  </span>
                  <span className={styles.historyStatus}>{r.itemsSettled}/{r.itemsTotal} settled</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {open && <SettleBox scopeKind={open.kind} scopeId={open.id} onClose={() => setOpen(null)} />}
    </Modal>
  );
}

/** The settle box modal for one scope: a recessed grey body carrying white section cards — a SUMMARY bar,
 *  COLLECT (deposit + balance) and PAY MEMBERS — plus close-out + CSV. The net is the settlement panel's net to
 *  artist summed over the scope, merch always included (ART-290 retired the "include merch" toggle). */
function SettleBox({ scopeKind, scopeId, onClose }: { scopeKind: string; scopeId: string; onClose: () => void }) {
  const [confirmReopen, setConfirmReopen] = useState(false);
  const q = useSettleBox(scopeKind, scopeId, true);
  const closeOut = useCloseOut();
  const reopen = useReopenScope();
  const box = q.data;

  const scopeWord = scopeKind === "run" ? "Run" : scopeKind === "tour" ? "Tour" : "Show";
  const settled = box?.itemsSettled ?? 0;
  const total = box?.itemsTotal ?? 0;
  const pct = total > 0 ? Math.round((settled / total) * 100) : 0;

  const members = box?.members ?? [];

  // A SHOW's sub is just its venue, which the title ("FRI SEP 25 · VENUE") already carries — redundant, so drop
  // it. A run/tour sub is the date range + show/travel/off-day counts, which the title does not, so keep it.
  const subtitle = scopeKind === "show" ? undefined : box?.sub || undefined;

  return (
    <Modal
      title={box?.title ?? "Settle"}
      subtitle={subtitle}
      size="xl"
      onClose={onClose}
      className={styles.settleModal}
      bodyLayout="flush"
      footer={
        box && (
          <div className={styles.foot}>
            <Button variant="ghost" size="sm" onClick={() => exportCsv(box)}>Export CSV</Button>
            {box.closedOut ? (
              <Button
                variant="ghost"
                size="sm"
                loading={reopen.isPending}
                onClick={() => setConfirmReopen(true)}
              >
                Reopen {scopeWord.toLowerCase()}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={!box.canCloseOut || closeOut.isPending}
                loading={closeOut.isPending}
                onClick={() => closeOut.mutate({ scopeKind, scopeId }, { onSuccess: onClose })}
              >
                Close out {scopeWord.toLowerCase()}
              </Button>
            )}
          </div>
        )
      }
    >
      {q.isLoading || !box ? (
        <div className={styles.loading}><Spinner size={22} /></div>
      ) : (
        <div className={styles.box}>
          <section className={styles.summaryCard}>
            <div className={styles.summaryTop}>
              <div className={styles.progressCol}>
                <div className={styles.progressHead}>
                  <span className={styles.progressLabel}>{settled} of {total} settled</span>
                  <span className={styles.netInline}>{scopeWord} net {usdCents(box.netCents)}</span>
                </div>
                <span className={styles.bar} aria-hidden="true">
                  <span
                    className={styles.barFill}
                    data-complete={total > 0 && settled >= total ? "" : undefined}
                    style={{ inlineSize: `${pct}%` }}
                  />
                </span>
              </div>
              <div className={styles.leftCol} data-owed={(box.leftCents ?? 0) > 0 ? "" : undefined}>
                <span className={styles.leftLabel}>Left to settle</span>
                <span className={styles.leftValue}>{usdCents(box.leftCents)}</span>
              </div>
            </div>
          </section>

          {(box.collect ?? []).length > 0 && (
            <section className={styles.sectionCard}>
              <header className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Collect</span>
              </header>
              <div className={styles.sectionRows}>
                {(box.collect ?? []).map((c) => (
                  <CollectRow key={`${c.showId}:${c.kind}`} item={c} />
                ))}
              </div>
            </section>
          )}

          {members.length > 0 && (
            <section className={styles.sectionCard}>
              <header className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Pay members</span>
              </header>
              <div className={styles.sectionRows}>
                {members.map((m) => (
                  <MemberRow key={m.personId} member={m} scopeKind={scopeKind} scopeId={scopeId} scopeTitle={box.title ?? "Payment"} />
                ))}
              </div>
            </section>
          )}

          {(box.expenses ?? []).length > 0 && (
            <section className={styles.sectionCard}>
              <header className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Expenses</span>
              </header>
              <div className={styles.sectionRows}>
                {(box.expenses ?? []).map((e) => (
                  <ExpenseRow key={`${e.showId}:${e.key}`} item={e} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
      {confirmReopen && (
        <ReopenModal
          noun={scopeWord.toLowerCase()}
          loading={reopen.isPending}
          onClose={() => setConfirmReopen(false)}
          onConfirm={() => reopen.mutate({ scopeKind, scopeId }, { onSuccess: () => setConfirmReopen(false) })}
        />
      )}
    </Modal>
  );
}

/** An EXPENSES line — a show's cost bucket (hotel/gas/…) or a custom line, with a "Mark paid" toggle. */
function ExpenseRow({ item }: { item: OutstandingExpenseItem }) {
  const mark = useMarkExpense();
  const paid = item.paidOn != null;
  const iso = item.date ?? "";
  const showId = item.showId ?? "";
  const key = item.key ?? "";
  return (
    <div className={styles.expenseLine}>
      <span className={styles.expenseMain}>
        <span className={styles.expenseLabel}>{item.venue || "Venue"} · {item.label}</span>
        {iso && (
          <span className={styles.expenseDate}>
            {dowOf(iso)} {dayOf(iso)}
            {/* ART-305 v2 — a Calendar expense names who paid it, so you know who to pay back. */}
            {item.addedByName ? ` · paid by ${item.addedByName}` : ""}
          </span>
        )}
      </span>
      <span className={styles.lineAmount}>{usdCents(item.amountCents)}</span>
      {paid ? (
        <button
          type="button"
          className={styles.paidPill}
          disabled={mark.isPending}
          onClick={() => mark.mutate({ showId, expenseKey: key, paid: false })}
        >
          Paid
        </button>
      ) : (
        <button
          type="button"
          className={styles.markPill}
          disabled={mark.isPending}
          onClick={() => mark.mutate({ showId, expenseKey: key, paid: true })}
        >
          Mark paid
        </button>
      )}
    </div>
  );
}

/** A COLLECT line — a venue deposit or balance, with a "Mark received" toggle (the one deposit field). */
function CollectRow({ item }: { item: OutstandingCollectItem }) {
  const mark = useMarkCollect();
  const received = item.receivedOn != null;
  const isDeposit = item.kind === "deposit";
  const iso = item.date ?? "";
  return (
    <div className={styles.line}>
      <span className={styles.lineMain}>
        <span className={styles.lineLabel}>{item.venue || "Venue"} · {isDeposit ? "deposit" : "balance"}</span>
        <span className={styles.lineSub}>
          {iso ? `${dowOf(iso)} ${dayOf(iso)} · ` : ""}{isDeposit ? "due on signing" : "at show"}
        </span>
      </span>
      <span className={styles.lineAmount}>{usdCents(item.amountCents)}</span>
      {received ? (
        <button
          type="button"
          className={styles.receivedPill}
          disabled={mark.isPending}
          onClick={() => mark.mutate({ showId: item.showId ?? "", kind: item.kind ?? "", received: false })}
        >
          Received
        </button>
      ) : (
        <button
          type="button"
          className={styles.markPill}
          disabled={mark.isPending}
          onClick={() => mark.mutate({ showId: item.showId ?? "", kind: item.kind ?? "", received: true })}
        >
          Mark received
        </button>
      )}
    </div>
  );
}

/** A PAY-MEMBERS line — the member's per-diem + day pay, "Mark paid" always, and "Pay with Venmo" when the
 *  member has a Venmo handle and the line is unpaid. */
function MemberRow({
  member, scopeKind, scopeId, scopeTitle,
}: {
  member: OutstandingMemberLine; scopeKind: string; scopeId: string; scopeTitle: string;
}) {
  const pay = useMarkMemberPaid();
  const unmark = useUnmarkMemberPaid();
  const [venmoOpen, setVenmoOpen] = useState(false);
  const paid = member.paidOn != null;
  const personId = member.personId ?? "";
  const perDiem = member.perDiemCents ?? 0;
  const dayPay = member.payCents ?? 0;
  const showPay = member.showPayCents ?? 0;
  const parts = [
    perDiem > 0 ? `${usdCents(perDiem)} per diem` : null,
    dayPay > 0 ? `${usdCents(dayPay)} travel` : null,
    showPay > 0 ? `${usdCents(showPay)} show` : null,
  ].filter(Boolean);
  const hasVenmo = !!member.venmoUsername;

  // "Pay with Venmo" only OPENS the pop-up — it does NOT mark paid, because the TM can still cancel in Venmo
  // (there is no completion callback, so nothing but their own confirmation can be the truth). The pop-up marks
  // paid on an explicit "Mark as paid".
  const undo = () => unmark.mutate({ scopeKind, scopeId, personId });

  return (
    <div className={styles.memberLine}>
      <PersonAvatar
        personId={personId}
        avatarMediaId={member.avatarMediaId}
        initials={initialsOf(member.name ?? "")}
        size="sm"
        shape="circle"
        className={styles.memberFace}
      />
      <span className={styles.lineMain}>
        <span className={styles.lineLabel}>{member.name}</span>
        <span className={styles.lineSub}>{parts.join(" · ")}</span>
      </span>
      <span className={styles.lineAmount}>{usdCents(member.amountCents)}</span>
      {paid ? (
        <button type="button" className={styles.paidPill} disabled={unmark.isPending} onClick={undo}>Paid</button>
      ) : (
        <span className={styles.lineActions}>
          {hasVenmo && (
            <button type="button" className={styles.venmoBtn} onClick={() => setVenmoOpen(true)}>
              Pay with <VenmoWordmark className={styles.venmoLogoSm} />
            </button>
          )}
          <button type="button" className={styles.markPill} disabled={pay.isPending}
            onClick={() => pay.mutate({ scopeKind, scopeId, personId, method: "cash" })}>
            Mark paid
          </button>
        </span>
      )}
      {venmoOpen && (
        <VenmoPayModal
          personId={personId}
          avatarMediaId={member.avatarMediaId}
          username={member.venmoUsername ?? ""}
          amountCents={member.amountCents ?? 0}
          name={member.name ?? "member"}
          scopeTitle={scopeTitle}
          confirming={pay.isPending}
          onConfirm={() => pay.mutate({ scopeKind, scopeId, personId, method: "venmo" })}
          onUndo={undo}
          onClose={() => setVenmoOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * The "Pay with Venmo" pop-up — the deep-link launcher (R8). We never touch funds: on mobile it opens the
 * Venmo APP pre-filled (via the iOS-safe location.assign seam), on desktop it shows a server-minted QR of the
 * web link + "Open venmo.com". The line is ALREADY marked paid (our flag is the source of truth); Undo reverts.
 */
function VenmoPayModal({
  personId, avatarMediaId, username, amountCents, name, scopeTitle, confirming, onConfirm, onUndo, onClose,
}: {
  personId: string; avatarMediaId: string | null | undefined;
  username: string; amountCents: number; name: string; scopeTitle: string;
  confirming: boolean; onConfirm: () => void; onUndo: () => void; onClose: () => void;
}) {
  // Not paid until the TM confirms they actually sent it — they can cancel inside Venmo, and there is no
  // callback, so this flag is the only truth. Confirm marks it; Undo reverts to the un-confirmed state.
  const [marked, setMarked] = useState(false);
  const note = venmoNote(scopeTitle);
  const payment = { username, amountCents, note };
  const native = isNative();
  const webLink = venmoWebLink(payment);
  const qr = useVenmoQr(webLink, !native);   // desktop only

  const openApp = () => window.location.assign(venmoDeepLink(payment));
  const openWeb = () => window.open(webLink, "_blank", "noopener,noreferrer");
  const confirm = () => { onConfirm(); setMarked(true); };
  const revert = () => { onUndo(); setMarked(false); };

  return (
    <Modal
      title="Pay with Venmo"
      hideHeader
      size="sm"
      bodyLayout="flush"
      className={styles.venmoModal}
      onClose={onClose}
      footer={
        marked ? (
          <div className={styles.venmoFoot}>
            <span className={styles.markedPaid}>Marked paid</span>
            <div className={styles.venmoFootActions}>
              <Button variant="ghost" size="sm" onClick={revert}>Undo</Button>
              <Button size="sm" onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className={styles.venmoFoot}>
            <div className={styles.venmoFootActions}>
              <DialogCancel size="sm" />
              <Button size="sm" loading={confirming} onClick={confirm}>Mark as paid</Button>
            </div>
          </div>
        )
      }
    >
      <div className={styles.quietHead}>
        <span className={styles.venmoTitle}>Pay with <VenmoWordmark className={styles.venmoLogoLg} /></span>
        <button type="button" className={styles.quietClose} aria-label="Close" onClick={onClose}>
          <Icon icon={X} size={20} />
        </button>
      </div>
      <div className={styles.venmoBody}>
        <PersonAvatar
          personId={personId}
          avatarMediaId={avatarMediaId}
          initials={initialsOf(name)}
          size="lg"
          shape="circle"
        />
        <p className={styles.venmoName}>{name}</p>
        <p className={styles.venmoHandle}>@{username}</p>
        <p className={styles.venmoAmount}>{usd2(amountCents)}</p>
        <span className={styles.venmoNotePill}>{note}</span>

        {native ? (
          <Button onClick={openApp}>Open Venmo</Button>
        ) : (
          <div className={styles.qrCard}>
            <div className={styles.qrSlot}>
              {qr.data?.dataUri ? (
                <img src={qr.data.dataUri} alt="Scan to pay with Venmo" width={88} height={88} />
              ) : (
                <span className={styles.qrPlaceholder}>QR CODE</span>
              )}
            </div>
            <span className={styles.orDivider} aria-hidden="true"><span>or</span></span>
            <Button variant="ghost" size="sm" onClick={openWeb}>Open venmo.com</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";
}

/** Dollars-and-cents ("$225.00") — the pay pop-up shows the exact amount, unlike the whole-dollar tiles. */
const usd2 = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Math.max(0, cents) / 100);

/** Client-side CSV of every line with its status and the date it was marked. */
function exportCsv(box: NonNullable<ReturnType<typeof useSettleBox>["data"]>) {
  const rows: string[][] = [["Type", "Who / what", "Amount", "Status", "Date"]];
  for (const c of box.collect ?? [])
    rows.push([c.kind ?? "", c.venue ?? "", cents(c.amountCents ?? 0), c.receivedOn ? "received" : "owed", c.receivedOn ?? ""]);
  for (const m of box.members ?? [])
    rows.push(["pay", m.name ?? "", cents(m.amountCents ?? 0), m.paidOn ? `paid (${m.method ?? ""})` : "unpaid", m.paidOn ?? ""]);
  for (const e of box.expenses ?? [])
    rows.push(["expense", `${e.venue ?? ""} · ${e.label ?? ""}`, cents(e.amountCents ?? 0), e.paidOn ? "settled" : "owed", e.paidOn ?? ""]);
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(box.title ?? "scope").replace(/[^\w]+/g, "-").toLowerCase()}-payments.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const cents = (c: number) => (c / 100).toFixed(2);
const csvCell = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
